// copied and edited from packages/ui/src/components/FlavorFinder/components/Results/FlavorFinderResults.tsx
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { FlavorFinderResult } from "../../types/FlavorFinderTypes";
import { FlavorFinderResetButton, FlavorFinderResultsButton } from "../Buttons/FlavorFinderButtons";
import { FlavorFinderFooter, FlavorFinderFooterSticky } from "../Footer";
import { FlavorFinderHeader } from "../Header/FlavorFinderHeader";
import { useFlavorFinderContext } from "../Provider/FlavorFinderContextProvider";
import { FlavorFinderResultCard } from "../ResultCard/FlavorFinderResultCard";
import { FlavorFinderResultSuggestionCard } from "../ResultSuggestionCard/FlavorFinderResultSuggestionCard";

import styles from "./FlavorFinderResults.module.scss";

type FlavorFinderResultProps = {
  results: FlavorFinderResult;
};

export const FlavorFinderResults: FC<FlavorFinderResultProps> = ({ results }) => {
  const { resetQuiz, flavorFinderData } = useFlavorFinderContext();
  const { results: quizResults } = results;
  const { finalActionCtaLabel, finalActionCtaLink, redoCtaLabel, result } = flavorFinderData;
  const { title, subtitle, flavorLabel, intensityLabel, coolingLabel, deviceSuggestion } = flavorFinderData.result;
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const [useResultsWithDevice, setUseResultsWithDevice] = useState<string>("");

  const buttonUrl = useMemo(() => {
    const { resultsLinkWithDevice, resultsLink } = results;
    const deviceSuggestionCardEnabled = result.deviceSuggestionCardEnabled;

    const isUseResultsWithDeviceYes = useResultsWithDevice === "yes" || !useResultsWithDevice;
    const isUseResultsWithDeviceNo = useResultsWithDevice === "no";

    if (deviceSuggestionCardEnabled) {
      if (isUseResultsWithDeviceYes && resultsLinkWithDevice) {
        return resultsLinkWithDevice;
      }
      if (isUseResultsWithDeviceNo && resultsLink) {
        return resultsLink;
      }
    } else if (resultsLink) {
      return resultsLink;
    }

    return finalActionCtaLink || "#";
  }, [useResultsWithDevice, results, finalActionCtaLink]);

  const handleNextStepClick = async () => {
    await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: finalActionCtaLabel,
      cta_location: "flavor-finder-results",
    });

    window.location.href = buttonUrl;
  };

  const handleSuggestionSelectionChange = (value: string) => {
    setUseResultsWithDevice(value);
  };

  const toggleActive = (index: number) => {
    setActiveIndex((prevIndex: number) => (prevIndex === index ? -1 : index));
  };

  useEffect(() => {
    dispatchTrackingEvent(TrackingEvents.TrackingFlavorFinderResult);
  }, []);

  return (
    <div className={styles.questionsWrapper} data-testid="flavor-finder-question">
      <div className={styles.questionContainer}>
        <div className={styles.question}>
          <FlavorFinderHeader title={title} subtitle={subtitle}>
            <FlavorFinderResetButton onClick={resetQuiz} label={redoCtaLabel} mobileHidden />
          </FlavorFinderHeader>
          <div className={styles.results}>
            {quizResults?.map((result, index) => (
              <FlavorFinderResultCard
                key={result.sku}
                result={result}
                flavorLabel={flavorLabel}
                intensityLabel={intensityLabel}
                coolingLabel={coolingLabel}
                isHiglighted={index === activeIndex}
                onClick={() => toggleActive(index)}
                index={index}
              />
            )) ?? null}
            {result.deviceSuggestionCardEnabled && (
              <FlavorFinderResultSuggestionCard
                description={deviceSuggestion.description}
                imgUrl={deviceSuggestion.imgUrl}
                useResultsWithDeviceLink={deviceSuggestion.defaultDeviceSuggestionState}
                onSelectionChange={handleSuggestionSelectionChange}
              />
            )}
          </div>
          <FlavorFinderFooter>
            <FlavorFinderResetButton onClick={resetQuiz} label={redoCtaLabel} className={styles.resetButton} />

            <FlavorFinderFooterSticky>
              <FlavorFinderResultsButton onClick={handleNextStepClick} label={finalActionCtaLabel} />
            </FlavorFinderFooterSticky>
          </FlavorFinderFooter>
        </div>
      </div>
    </div>
  );
};
